import { createContext, FC, ReactNode, useContext, useMemo, useState } from 'react'
import { LogisticBoxView, SolicitarInsumosForm } from '../models'

interface LogisticBoxContextValue {
  activeView: LogisticBoxView
  setActiveView: (v: LogisticBoxView) => void
  // Tienda seleccionada al navegar a un detalle.
  // `selectedStore` se usa para el título (nombre); `selectedStoreId` para los fetch.
  selectedStore: string | null
  selectedStoreId: number | null
  setSelectedStore: (name: string | null, id?: number | null) => void
  goToDetail: (view: LogisticBoxView, store?: string, storeId?: number | null) => void
  calendarOpen: boolean
  openCalendar: () => void
  closeCalendar: () => void
  solicitarPrefill: SolicitarInsumosForm | null
  openSolicitar: (data: SolicitarInsumosForm) => void
  closeSolicitar: () => void
  // Tracking de envío de insumos seleccionado (vista tracking-insumos)
  selectedTracking: string | null
  goToShipmentTracking: (trackingNumber: string) => void
  // Tracking de una recolección de cajas (sub-página "Ver tracking" en Recolecciones)
  goToTrackingDetail: (trackingNumber: string) => void
  // Envío (collection) seleccionado para ver sus equipos (vista envio-detalle)
  selectedCollectionId: number | null
  selectedBoxSeal: string | null
  selectedBoxSeal2: string | null
  selectedBoxKey: string | null
  selectedBoxTracking: string | null
  goToEnvio: (idCollection: number, store?: string, seal?: string | null, seal2?: string | null, boxKey?: string | null, tracking?: string | null) => void
  // Señal para refrescar las vistas de insumos tras crear/aprobar/rechazar
  supplyRefresh: number
  bumpSupplyRefresh: () => void
}

const LogisticBoxContext = createContext<LogisticBoxContextValue | undefined>(undefined)

interface Props { children: ReactNode }

export const LogisticBoxProvider: FC<Props> = ({ children }) => {
  const [activeView, setActiveView] = useState<LogisticBoxView>('dashboard')
  const [selectedStore, _setSelectedStore] = useState<string | null>(null)
  const [selectedStoreId, setSelectedStoreId] = useState<number | null>(null)
  const [calendarOpen, setCalendarOpen] = useState(false)
  const [solicitarPrefill, setSolicitarPrefill] = useState<SolicitarInsumosForm | null>(null)
  const [selectedTracking, setSelectedTracking] = useState<string | null>(null)
  const [selectedCollectionId, setSelectedCollectionId] = useState<number | null>(null)
  const [selectedBoxSeal, setSelectedBoxSeal] = useState<string | null>(null)
  const [selectedBoxSeal2, setSelectedBoxSeal2] = useState<string | null>(null)
  const [selectedBoxKey, setSelectedBoxKey] = useState<string | null>(null)
  const [selectedBoxTracking, setSelectedBoxTracking] = useState<string | null>(null)
  const [supplyRefresh, setSupplyRefresh] = useState(0)

  const setSelectedStore = (name: string | null, id?: number | null): void => {
    _setSelectedStore(name)
    if (id !== undefined) setSelectedStoreId(id)
  }

  const value = useMemo<LogisticBoxContextValue>(() => ({
    activeView,
    setActiveView,
    selectedStore,
    selectedStoreId,
    setSelectedStore,
    goToDetail: (view, store, storeId) => {
      if (store !== undefined) _setSelectedStore(store)
      if (storeId !== undefined) setSelectedStoreId(storeId)
      setActiveView(view)
    },
    calendarOpen,
    openCalendar: () => setCalendarOpen(true),
    closeCalendar: () => setCalendarOpen(false),
    solicitarPrefill,
    openSolicitar: (data) => setSolicitarPrefill(data),
    closeSolicitar: () => setSolicitarPrefill(null),
    selectedTracking,
    goToShipmentTracking: (trackingNumber) => {
      setSelectedTracking(trackingNumber)
      setActiveView('tracking-insumos')
    },
    goToTrackingDetail: (trackingNumber) => {
      setSelectedTracking(trackingNumber)
      setActiveView('recoleccion-tracking')
    },
    selectedCollectionId,
    selectedBoxSeal,
    selectedBoxSeal2,
    selectedBoxKey,
    selectedBoxTracking,
    goToEnvio: (idCollection, store, seal, seal2, boxKey, tracking) => {
      setSelectedCollectionId(idCollection)
      setSelectedBoxSeal(seal ?? null)
      setSelectedBoxSeal2(seal2 ?? null)
      setSelectedBoxKey(boxKey ?? null)
      setSelectedBoxTracking(tracking ?? null)
      if (store !== undefined) _setSelectedStore(store)
      setActiveView('envio-detalle')
    },
    supplyRefresh,
    bumpSupplyRefresh: () => setSupplyRefresh((n) => n + 1)
  }), [activeView, selectedStore, selectedStoreId, calendarOpen, solicitarPrefill, selectedTracking, selectedCollectionId, selectedBoxSeal, selectedBoxSeal2, selectedBoxKey, selectedBoxTracking, supplyRefresh])

  return (
    <LogisticBoxContext.Provider value={value}>
      {children}
    </LogisticBoxContext.Provider>
  )
}

export const useLogisticBox = (): LogisticBoxContextValue => {
  const ctx = useContext(LogisticBoxContext)
  if (!ctx) throw new Error('useLogisticBox must be used within LogisticBoxProvider')
  return ctx
}
